// @ts-nocheck
import React, { useEffect, useRef } from "react";
import { Button, message } from "antd";
import { LeftOutlined } from "@ant-design/icons";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import dayjs from "dayjs";
import {
  pdf,
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
} from "@react-pdf/renderer";
import { createOutlookDraftFromTemplate } from "../../utils/API";
import { openOutlookDraft } from "../../utils/outlook";
import PageLogoutButton from "../../Components/PageLogoutButton";

/* ─── helpers ──────────────────────────────────────── */
const parseNumber = (v: any) => {
  const n = Number(String(v || "").replace(/[^0-9.-]/g, ""));
  return Number.isNaN(n) ? 0 : n;
};
const fmt = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(v || 0);
const fmtDate = (v: any) => {
  if (!v) return "-";
  const p = dayjs(v);
  return p.isValid() ? p.format("MMMM D, YYYY") : String(v);
};
const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => {
      const res = String(r.result || "");
      const b64 = res.includes(",") ? res.split(",")[1] : "";
      b64 ? resolve(b64) : reject(new Error("base64 failed"));
    };
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });

/* ─── colours ──────────────────────────────────────── */
const BLUE       = "#1565c0";   // primary brand blue
const NAVY       = "#0d47a1";   // darker shade (10% darker than BLUE)
const LIGHT_BLUE = "#e3f0fc";   // very light tint for info bar background
const TH_BG      = "#1565c0";   // table header — same brand blue
const GREY_LINE  = "#cccccc";
const GREY_LABEL = "#666666";

// Notes shown in both the PDF and the on-screen preview
const QUOTE_NOTES: { text: string; bold?: boolean }[] = [
  { text: "All prices are in USD. Shipping and insurance are billed at cost." },
  { text: "Pricing is based on purchasing all quoted line items as a complete package. Individual items cannot be ordered separately under this quotation.", bold: true },
  { text: "All items are subject to prior sale." },
  { text: "The quoted calibration includes a standard calibration certificate. Calibration with measurement data, NIST traceability, ISO/IEC 17025 accreditation, or ANSI/NCSL Z540 compliance is available at additional cost." },
  { text: "Lead times are estimates beginning at order acceptance and include calibration, adjustments, and shipping time." },
  { text: "Title and risk of loss pass to the buyer upon carrier pickup in Webster, MA (FCA Origin)." },
  { text: "Payment terms are Net 30 from the shipment date." },
];

/* ─── PDF styles ───────────────────────────────────── */
const S = StyleSheet.create({
  page:          { paddingTop: 0, paddingBottom: 6, paddingHorizontal: 28, fontFamily: "Helvetica", fontSize: 9, backgroundColor: "#fff" },

  // Top accent line
  topBar:        { height: 16, backgroundColor: BLUE, marginTop: 22, marginBottom: 14, marginLeft: 28, marginRight: 28, flexDirection: "row", justifyContent: "center", alignItems: "center" },
  topBarText:    { fontSize: 7.5, color: "#fff", fontFamily: "Helvetica-Bold", textAlign: "center" },

  // Header
  headerRow:     { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 },
  logoImg:       { width: 130, height: 52, objectFit: "contain" },
  headerRight:   { textAlign: "right", fontSize: 8.5, lineHeight: 1.55, color: "#000" },

  // Divider
  divider:       { height: 1, backgroundColor: BLUE, marginBottom: 8 },

  // Quote title row
  quoteTitle:    { fontSize: 20, fontFamily: "Helvetica-Bold", color: "#000", marginBottom: 2 },
  quoteSub:      { fontSize: 8.5, color: GREY_LABEL, marginBottom: 7 },

  // Blue info bar
  infoBar:       { backgroundColor: LIGHT_BLUE, paddingVertical: 5, paddingHorizontal: 10, marginBottom: 8, flexDirection: "row", justifyContent: "center", alignItems: "center" },
  infoBarText:   { fontSize: 8.5, color: BLUE, fontFamily: "Helvetica-Bold" },
  infoBarLink:   { fontSize: 8.5, color: BLUE },
  infoBarLinkBold:{ fontSize: 8.5, color: BLUE, fontFamily: "Helvetica-Bold" },

  // Two-column info
  twoCol:        { flexDirection: "row", marginBottom: 4 },
  infoBox:       { flex: 1, padding: "5 8" },
  infoBoxLeft:   { marginRight: 5 },
  infoBoxRight:  { marginLeft: 5 },
  boxLabel:      { fontSize: 7, fontFamily: "Helvetica-Bold", color: GREY_LABEL, letterSpacing: 0.8, marginBottom: 5, textTransform: "uppercase" },
  boxCompany:    { fontSize: 10, fontFamily: "Helvetica-Bold", color: "#000", marginBottom: 2 },
  boxAttn:       { fontSize: 8.5, color: "#000" },
  detailRow:     { flexDirection: "row", marginBottom: 3 },
  detailKey:     { fontSize: 8.5, color: GREY_LABEL, width: 90 },
  detailVal:     { fontSize: 8.5, color: "#000", flex: 1 },

  // Cal / Warranty / Condition bar
  cwcRow:        { flexDirection: "row", marginBottom: 5, backgroundColor: "#f5f5f5" },
  cwcCell:       { flex: 1, paddingVertical: 4, paddingHorizontal: 6 },
  cwcCellLast:   { flex: 1, paddingVertical: 4, paddingHorizontal: 6 },
  cwcKey:        { fontSize: 7.5, fontFamily: "Helvetica-Bold", color: GREY_LABEL, marginBottom: 1 },
  cwcVal:        { fontSize: 8, color: "#000" },

  // Table
  tableWrap:     { marginBottom: 5 },
  tableHead:     { flexDirection: "row", backgroundColor: "#e8e8e8" },
  tableRow:      { flexDirection: "row" },
  thCell:        { color: "#333", fontFamily: "Helvetica-Bold", fontSize: 7, padding: "5 4", textAlign: "center" },
  tdCell:        { fontSize: 8, padding: "3 4", borderBottom: "1px solid #e0e0e0", textAlign: "center" },
  tdItemCell:    { fontSize: 8, padding: "3 5", borderBottom: "1px solid #e0e0e0", textAlign: "left" },
  tdSubItemCell: { fontSize: 7.5, padding: "2 5", borderBottom: "1px solid #e0e0e0", textAlign: "left", color: "#444" },

  // Totals
  totalsWrap:    { alignItems: "flex-end", marginBottom: 5 },
  totalLineRow:  { flexDirection: "row", marginBottom: 1.5 },
  totalLineKey:  { fontSize: 8.5, color: GREY_LABEL, width: 130, textAlign: "right", paddingRight: 8 },
  totalLineVal:  { fontSize: 8.5, color: "#000", width: 80, textAlign: "right" },
  totalDivider:  { width: 210, height: 1, backgroundColor: "#000", marginVertical: 3 },
  totalMainRow:  { flexDirection: "row", alignItems: "center", marginTop: 2 },
  totalMainKey:  { fontSize: 10, fontFamily: "Helvetica-Bold", width: 130, textAlign: "right", paddingRight: 8 },
  totalMainVal:  { fontSize: 11, fontFamily: "Helvetica-Bold", width: 80, textAlign: "right" },

  // Warranty banner
  warrantyBox:   { border: "1px solid #cccccc", padding: "7 14", textAlign: "center", marginBottom: 5 },
  warLine:       { fontSize: 9, fontFamily: "Helvetica", marginBottom: 4, textAlign: "center", color: "#000" },
  warLineFirst:  { fontSize: 10, fontFamily: "Helvetica-Bold", marginBottom: 4, textAlign: "center", color: "#000" },
  warLink:       { fontSize: 9, color: BLUE, textAlign: "center" },

  // Notes
  notesBox:      { padding: "6 10", backgroundColor: "#f5f5f5" },
  notesTitle:    { fontSize: 8, fontFamily: "Helvetica-Bold", color: GREY_LABEL, marginBottom: 4, letterSpacing: 0.5 },
  noteItem:      { fontSize: 7.5, lineHeight: 1.45, marginBottom: 1.5, color: "#000" },

  // Footer
  footerLine:    { borderTop: "1px solid #cccccc", marginTop: 5, paddingTop: 5, textAlign: "center", fontSize: 8, color: "#555" },
  bbbFooterLine: { fontSize: 8, fontFamily: "Helvetica-Bold", color: BLUE, textAlign: "center", marginTop: 3 },
});

/* ─── column widths ───────────────────────────────── */
//  #    ITEM    OPT   QTY  CALIBRATION  UNIT PRICE  EXTENDED
const CW = ["5%","24%","16%","6%","14%","22%","13%"];

/* ─── PDF Document ────────────────────────────────── */
const QuotePDFDoc = ({
  quoteId, preparedBy, preparedDate, validDate,
  contactName, companyName, paymentTerms, incoterms,
  tableRows, equipSubtotal, grandTotal,
  logoBase64,
}: any) => (
  <Document>
    <Page size="LETTER" style={S.page}>

      {/* TOP ACCENT BAR */}
      <View style={S.topBar}>
        <Text style={S.topBarText}>BBB A+ Accredited  ·  21 Years in Business  ·  Trusted Test Equipment Partner</Text>
      </View>

      {/* HEADER */}
      <View style={S.headerRow}>
        {logoBase64
          ? <Image src={`data:image/jpeg;base64,${logoBase64}`} style={S.logoImg} />
          : <Text style={{ fontSize: 14, fontFamily: "Helvetica-Bold", color: BLUE }}>Alliance Test</Text>
        }
        <View style={S.headerRight}>
          <Text>Webster, MA 01570 USA</Text>
          <Text>508-671-9970</Text>
          <Text>orders@alliancetesteq.com</Text>
          <Text style={{ color: BLUE }}>www.alliancetesteq.com</Text>
        </View>
      </View>


      {/* QUOTE TITLE */}
      <Text style={S.quoteTitle}>Quote {quoteId}</Text>
      <Text style={S.quoteSub}>Prepared {preparedDate}  ·  Valid through {validDate}</Text>

      {/* BLUE INFO BAR */}
      <View style={S.infoBar}>
        <Text style={S.infoBarLinkBold}>Need it fast?</Text>
        <Text style={[S.infoBarLink, { marginLeft: 4 }]}>Ask about expedited service</Text>
        <Text style={[S.infoBarLink, { color: BLUE }]}>  ·  </Text>
        <Text style={S.infoBarText}>We accept all major credit cards</Text>
      </View>

      {/* PREPARED FOR / QUOTE DETAILS */}
      <View style={S.twoCol}>
        <View style={[S.infoBox, S.infoBoxLeft]}>
          <Text style={S.boxLabel}>Prepared For</Text>
          <Text style={S.boxCompany}>{companyName || "—"}</Text>
          {contactName ? <Text style={S.boxAttn}>Attn: {contactName}</Text> : null}
        </View>
        <View style={[S.infoBox, S.infoBoxRight]}>
          <Text style={S.boxLabel}>Quote Details</Text>
          {[
            ["Prepared by",   preparedBy    || "—"],
            ["Payment terms", paymentTerms  || "Net 30 on approved credit"],
            ["Incoterms",     incoterms     || "FCA Webster, MA"],
          ].map(([k, v]) => (
            <View key={k} style={S.detailRow}>
              <Text style={S.detailKey}>{k}</Text>
              <Text style={S.detailVal}>{v}</Text>
            </View>
          ))}
        </View>
      </View>

      {/* CAL / WARRANTY / CONDITION BAR */}
      <View style={S.cwcRow}>
        <View style={S.cwcCell}>
          <Text style={S.cwcKey}>Calibration</Text>
          <Text style={S.cwcVal}>Standard calibration certificate</Text>
        </View>
        <View style={S.cwcCell}>
          <Text style={S.cwcKey}>Warranty</Text>
          <Text style={S.cwcVal}>90 days parts &amp; labor</Text>
        </View>
        <View style={S.cwcCellLast}>
          <Text style={S.cwcKey}>Condition</Text>
          <Text style={S.cwcVal}>Used / refurbished unless noted</Text>
        </View>
      </View>

      {/* TABLE */}
      <View style={S.tableWrap}>
        {/* Header */}
        <View style={S.tableHead}>
          {["#","ITEM","OPT","QTY","CALIBRATION","UNIT PRICE","EXTENDED"].map((h, i) => (
            <View key={h} style={[S.thCell, { width: CW[i] }]}>
              <Text>{h}</Text>
            </View>
          ))}
        </View>

        {tableRows.map((row: any, idx: number) => {
          const bg = idx % 2 === 0 ? "#fff" : "#f5f8fc";
          return (
            <View key={row.key} style={[S.tableRow, { backgroundColor: bg }]}>
              <View style={[S.tdCell, { width: CW[0] }]}><Text>{idx + 1}</Text></View>
              <View style={[S.tdItemCell, { width: CW[1] }]}>
                <Text style={{ fontFamily: "Helvetica-Bold" }}>{row.item}</Text>
                {row.description ? <Text style={{ fontSize: 7, color: "#666" }}>{row.description}</Text> : null}
              </View>
              <View style={[S.tdCell, { width: CW[2], textAlign: "left" }]}><Text>{row.opt || "—"}</Text></View>
              <View style={[S.tdCell, { width: CW[3] }]}><Text>{row.qty}</Text></View>
              <View style={[S.tdCell, { width: CW[4] }]}><Text>{row.calibration}</Text></View>
              <View style={[S.tdCell, { width: CW[5] }]}><Text>{row.unitPrice}</Text></View>
              <View style={[S.tdCell, { width: CW[6] }]}><Text style={{ fontFamily: "Helvetica-Bold" }}>{row.extended}</Text></View>
            </View>
          );
        })}
      </View>

      {/* TOTALS */}
      <View style={S.totalsWrap}>
        <View style={S.totalLineRow}>
          <Text style={S.totalLineKey}>Subtotal</Text>
          <Text style={S.totalLineVal}>{fmt(equipSubtotal)}</Text>
        </View>
        <View style={S.totalLineRow}>
          <Text style={S.totalLineKey}>Freight &amp; insurance</Text>
          <Text style={S.totalLineVal}>At cost</Text>
        </View>
        <View style={S.totalLineRow}>
          <Text style={S.totalLineKey}>Sales tax</Text>
          <Text style={S.totalLineVal}>Per certificate on file</Text>
        </View>
        <View style={S.totalDivider} />
        <View style={S.totalMainRow}>
          <Text style={S.totalMainKey}>Total</Text>
          <Text style={S.totalMainVal}>{fmt(grandTotal)}</Text>
        </View>
      </View>

      {/* WARRANTY BANNER */}
      <View style={S.warrantyBox}>
        <Text style={S.warLineFirst}>Everything we sell includes a 90-day warranty.</Text>
        <Text style={S.warLine}>Want to upgrade to a 12-month warranty? Contact us and we'll extend it for you.</Text>
        <Text style={S.warLine}>Need to email an order? Send it to <Text style={S.warLink}>orders@alliancetesteq.com</Text></Text>
        <Text style={S.warLine}>
          For more details: <Text style={S.warLink}>www.alliancetesteq.com/contact/warranty-terms</Text>
        </Text>
      </View>

      {/* NOTES */}
      <View style={S.notesBox}>
        <Text style={S.notesTitle}>NOTES</Text>
        {QUOTE_NOTES.map((note, i) => (
          <Text key={i} style={note.bold ? [S.noteItem, { fontFamily: "Helvetica-Bold" }] : S.noteItem}>• {note.text}</Text>
        ))}
      </View>

      {/* FOOTER */}
      <View style={S.footerLine}>
        <Text>Alliance Test Equipment, Inc.  ·  Webster, MA 01570  ·  508-671-9970  ·  orders@alliancetesteq.com</Text>
      </View>

    </Page>
  </Document>
);

/* ─── main component ──────────────────────────────── */
const QuoteForm = () => {
  const navigate       = useNavigate();
  const location       = useLocation();
  const [searchParams] = useSearchParams();
  const runAutoFlowRef = useRef(false);
  const Logo_Main      = require("../../Assets/logo.png");

  const statePayload    = (location.state || {}) as any;
  const quoteState      = statePayload.quoteFormSnapshot || {};
  const rawRows         = Array.isArray(statePayload.tableDataSnapshot) ? statePayload.tableDataSnapshot : [];
  const restoredFilters = statePayload.restoredFilters ?? {};

  const quoteId      = String(quoteState.quoteId      || searchParams.get("quoteId") || "").trim();
  const preparedBy   = String(quoteState.representativeName || "Sean Anderson").trim();
  const contactName  = String(quoteState.contactName   || "").trim();
  const companyName  = String(quoteState.companyName   || "").trim();
  const paymentTerms = String(quoteState.paymentTerms  || "Net 30 on approved credit").trim();
  const incoterms    = String(quoteState.incoterms     || "FCA Webster, MA").trim();

  const todayDayjs   = dayjs();
  const preparedDate = todayDayjs.format("MMMM D, YYYY");
  const validDate    = todayDayjs.add(30, "day").format("MMMM D, YYYY");

  /* Build table rows */
  const tableRows = rawRows.map((row: any, idx: number) => {
    const qty     = Number(row.qty) || 1;
    const price   = parseNumber(row.price);
    const stdCal  = parseNumber(row.stdCal);
    const brand   = String(row.brand || row.manufacturer || "").trim();
    const model   = String(row.model || "").trim();
    const item    = [brand, model].filter(Boolean).join(" / ") || "—";
    return {
      key:         String(row.key || idx + 1),
      item,
      description: String(row.description || "").trim(),
      opt:         String(row.options || "").trim() || "—",
      qty,
      // CALIBRATION column: shows the stdCal price between QTY and UNIT PRICE
      calibration: fmt(stdCal),
      unitPrice:   fmt(price),
      // Extended includes calibration price so it all adds up in one column
      extended:    fmt(qty * (price + stdCal)),
    };
  });

  const equipSubtotal = tableRows.reduce((s: number, r: any) => s + parseNumber(r.extended), 0);
  const grandTotal    = equipSubtotal; // cal is already folded into extended per row

  /* Logo → base64 */
  const getLogoBase64 = async (): Promise<string> =>
    new Promise((resolve) => {
      const img = new window.Image();
      img.crossOrigin = "anonymous";
      img.onload = () => {
        const c = document.createElement("canvas");
        c.width = img.width; c.height = img.height;
        const ctx = c.getContext("2d")!;
        ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, c.width, c.height);
        ctx.drawImage(img, 0, 0);
        resolve(c.toDataURL("image/jpeg", 1.0).split(",")[1]);
      };
      img.onerror = () => resolve("");
      img.src = Logo_Main;
    });

  const buildPdfBlob = async () => {
    const logoBase64 = await getLogoBase64();
    const doc = (
      <QuotePDFDoc
        quoteId={quoteId}
        preparedBy={preparedBy}
        preparedDate={preparedDate}
        validDate={validDate}
        contactName={contactName}
        companyName={companyName}
        paymentTerms={paymentTerms}
        incoterms={incoterms}
        tableRows={tableRows}
        equipSubtotal={equipSubtotal}
        grandTotal={grandTotal}
        logoBase64={logoBase64}
      />
    );
    const blob = await pdf(doc).toBlob();
    return { blob, fileName: `AllianceTest-Quote-${quoteId}.pdf` };
  };

  const handleGeneratePdf = async () => {
    try {
      message.loading({ key: "pdf", content: "Generating PDF...", duration: 0 });
      const { blob, fileName } = await buildPdfBlob();
      const url = URL.createObjectURL(blob);
      const a   = document.createElement("a");
      a.href = url; a.download = fileName; a.click();
      URL.revokeObjectURL(url);
      message.destroy("pdf");
    } catch {
      message.destroy("pdf");
      message.error("PDF generation failed.");
    }
  };

  useEffect(() => {
    const mode = String(searchParams.get("mode") || "").toLowerCase();
    if (mode !== "send-fq" || !statePayload.autoSendFQ || runAutoFlowRef.current) return;
    runAutoFlowRef.current = true;
    (async () => {
      try {
        message.loading({ key: "fq", content: "Preparing quote PDF...", duration: 0 });
        const { blob, fileName } = await buildPdfBlob();
        const base64 = await blobToBase64(blob);
        await new Promise(r => setTimeout(r, Number(statePayload.delayMs) || 1500));
        const draft = await createOutlookDraftFromTemplate({
          templateSlug: statePayload.templateSlug || "quote-send-customer",
          quoteId:      quoteId || undefined,
          to:           statePayload.toEmail || undefined,
          launchMode:   statePayload.launchMode || "desktop",
          attachments:  [{ name: fileName, fileName, contentType: "application/pdf", contentBase64: base64 }],
        });
        await openOutlookDraft(draft);
        message.destroy("fq");
        await new Promise(r => setTimeout(r, 5000));
        navigate(`/Quotes?quoteId=${encodeURIComponent(quoteId)}`, { state: { restoredFilters } });
      } catch (err: any) {
        message.error({ key: "fq", content: err?.response?.data?.message || "Failed to prepare PDF/draft." });
      }
    })();
  }, [quoteId, searchParams, statePayload]);

  /* ─── link style reused in several places ─────────── */
  const linkStyle: React.CSSProperties = { color: BLUE, textDecoration: "underline", cursor: "pointer" };

  return (
    <div style={{ backgroundColor: "#e8edf2", minHeight: "100vh" }}>

      {/* ── Toolbar ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 16px", backgroundColor: "#fff", borderBottom: "1px solid #ddd", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
        <Button icon={<LeftOutlined />} size="small" onClick={() => navigate(`/Quotes?quoteId=${encodeURIComponent(quoteId)}`, { state: { restoredFilters } })}>Back</Button>
        <PageLogoutButton />
      </div>

      {/* ── Paper + side button ── */}
      <div style={{ padding: "28px 20px 56px", display: "flex", justifyContent: "center", alignItems: "flex-start", gap: 16 }}>
        <div style={{ width: 760, backgroundColor: "#fff", boxShadow: "0 4px 20px rgba(0,0,0,0.14)", fontFamily: "Arial, sans-serif", fontSize: 12, color: "#000" }}>

          {/* WHITE SPACE ABOVE BLUE BAR */}
          <div style={{ height: 28 }} />

          {/* TOP ACCENT BAR */}
          <div style={{ minHeight: 18, backgroundColor: BLUE, margin: "0 28px", display: "flex", alignItems: "center", justifyContent: "center", padding: "3px 8px" }}>
            <span style={{ color: "#fff", fontWeight: "bold", fontSize: 10, letterSpacing: 0.3, textAlign: "center" }}>
              BBB A+ Accredited &nbsp;·&nbsp; 21 Years in Business &nbsp;·&nbsp; Trusted Test Equipment Partner
            </span>
          </div>

          {/* HEADER SECTION */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 28px 10px" }}>
            <img src={Logo_Main} alt="Alliance Test" style={{ height: 58, objectFit: "contain" }} />
            <div style={{ textAlign: "right", fontSize: 11, lineHeight: "1.6", color: "#000" }}>
              <div>Webster, MA 01570 USA</div>
              <div>508-671-9970</div>
              <div>orders@alliancetesteq.com</div>
              <div style={{ color: BLUE }}>www.alliancetesteq.com</div>
            </div>
          </div>

          <div style={{ padding: "0 28px 24px" }}>

            {/* QUOTE TITLE */}
            <div style={{ fontSize: 26, fontWeight: "bold", color: "#000", marginBottom: 3, lineHeight: 1.1 }}>
              Quote {quoteId}
            </div>
            <div style={{ fontSize: 11, color: "#666", marginBottom: 10 }}>
              Prepared {preparedDate} &nbsp;·&nbsp; Valid through {validDate}
            </div>

            {/* INFO BAR */}
            <div style={{ backgroundColor: LIGHT_BLUE, padding: "7px 16px", marginBottom: 12, textAlign: "center", fontSize: 11.5 }}>
              <span style={{ color: BLUE, fontWeight: "bold" }}>Need it fast?</span>
              {" "}<span style={{ color: BLUE }}>Ask about expedited service</span>
              <span style={{ color: BLUE }}> &nbsp;·&nbsp; </span>
              <strong style={{ color: BLUE }}>We accept all major credit cards</strong>
            </div>

            {/* PREPARED FOR / QUOTE DETAILS */}
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <div style={{ flex: 1, padding: "8px 10px" }}>
                <div style={{ fontSize: 8.5, fontWeight: "bold", color: "#888", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Prepared For</div>
                <div style={{ fontSize: 13, fontWeight: "bold", marginBottom: 3 }}>{companyName || "—"}</div>
                {contactName && <div style={{ fontSize: 11, color: "#333" }}>Attn: {contactName}</div>}
              </div>
              <div style={{ flex: 1, padding: "8px 10px" }}>
                <div style={{ fontSize: 8.5, fontWeight: "bold", color: "#888", letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6 }}>Quote Details</div>
                {([
                  ["Prepared by",   preparedBy],
                  ["Payment terms", paymentTerms || "Net 30 on approved credit"],
                  ["Incoterms",     incoterms    || "FCA Webster, MA"],
                ] as [string,string][]).map(([k, v]) => (
                  <div key={k} style={{ display: "flex", marginBottom: 4, fontSize: 11 }}>
                    <span style={{ color: "#777", width: 100, flexShrink: 0 }}>{k}</span>
                    <span style={{ fontWeight: "500" }}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CALIBRATION / WARRANTY / CONDITION — label and value on same line */}
            <div style={{ display: "flex", marginBottom: 12, fontSize: 11, backgroundColor: "#f5f5f5" }}>
              {[
                ["Calibration", "Standard calibration certificate"],
                ["Warranty",    "90 days parts & labor"],
                ["Condition",   "Used / refurbished unless noted"],
              ].map(([label, val], i, arr) => (
                <div key={label} style={{ flex: 1, padding: "5px 8px" }}>
                  <span style={{ fontWeight: "bold", color: "#555" }}>{label}: </span>
                  <span style={{ color: "#000" }}>{val}</span>
                </div>
              ))}
            </div>

            {/* LINE ITEMS TABLE */}
            <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 10, tableLayout: "fixed" }}>
              <colgroup>{CW.map((w, i) => <col key={i} style={{ width: w }} />)}</colgroup>
              <thead>
                <tr>
                  {["#","ITEM","OPT","QTY","CALIBRATION","UNIT PRICE","EXTENDED"].map(h => (
                    <th key={h} style={{ backgroundColor: "#e8e8e8", color: "#333", fontWeight: "bold", fontSize: 10, padding: "7px 4px", textAlign: "center", borderBottom: "1px solid #ccc", letterSpacing: 0.3 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row: any, idx: number) => {
                  const bg = idx % 2 === 0 ? "#fff" : "#f9f9f9";
                  const td: React.CSSProperties = { fontSize: 11, padding: "5px 4px", textAlign: "center", borderBottom: "1px solid #e8e8e8", backgroundColor: bg };
                  return (
                    <tr key={row.key}>
                      <td style={td}>{idx + 1}</td>
                      <td style={{ ...td, textAlign: "left", padding: "5px 7px", fontWeight: "600" }}>
                        <div>{row.item}</div>
                        {row.description && <div style={{ fontSize: 9.5, color: "#666", fontWeight: "normal", marginTop: 2 }}>{row.description}</div>}
                      </td>
                      <td style={{ ...td, textAlign: "left", wordBreak: "break-word", whiteSpace: "normal", padding: "5px 6px" }}>{row.opt}</td>
                      <td style={td}>{row.qty}</td>
                      <td style={td}>{row.calibration}</td>
                      <td style={td}>{row.unitPrice}</td>
                      <td style={{ ...td, fontWeight: "bold" }}>{row.extended}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {/* TOTALS */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", marginBottom: 14 }}>
              {([
                ["Subtotal",            fmt(equipSubtotal), false],
                ["Freight & insurance", "At cost",          true ],
                ["Sales tax",           "Per certificate on file", true],
              ] as [string, string, boolean][]).map(([k, v]) => (
                <div key={k} style={{ display: "flex", marginBottom: 3, minWidth: 320 }}>
                  <span style={{ flex: 1, textAlign: "right", paddingRight: 14, fontSize: 11, color: "#555" }}>{k}</span>
                  <span style={{ width: 130, textAlign: "right", fontSize: 11, color: "#000", whiteSpace: "nowrap" }}>{v}</span>
                </div>
              ))}
              <div style={{ width: 320, height: 1, backgroundColor: "#999", margin: "5px 0" }} />
              <div style={{ display: "flex", alignItems: "center", minWidth: 320 }}>
                <span style={{ flex: 1, textAlign: "right", paddingRight: 14, fontSize: 12, fontWeight: "bold", color: "#000" }}>Total</span>
                <span style={{ width: 130, textAlign: "right", fontSize: 13, fontWeight: "bold", display: "inline-block" }}>{fmt(grandTotal)}</span>
              </div>
            </div>

            {/* WARRANTY BANNER */}
            <div style={{ border: "1px solid #ccc", padding: "12px 20px", textAlign: "center", marginBottom: 10, fontSize: 12, lineHeight: "1.9" }}>
              <div style={{ fontWeight: "bold", fontSize: 13 }}>Everything we sell includes a 90-day warranty.</div>
              <div>Want to upgrade to a 12-month warranty? Contact us and we'll extend it for you.</div>
              <div>Need to email an order? Send it to <a href="mailto:orders@alliancetesteq.com" style={linkStyle}>orders@alliancetesteq.com</a></div>
              <div>For more details: <a href="http://www.alliancetesteq.com/contact/warranty-terms" style={linkStyle}>www.alliancetesteq.com/contact/warranty-terms</a></div>
            </div>

            {/* NOTES */}
            <div style={{ padding: "10px 14px", fontSize: 10, lineHeight: "1.55", backgroundColor: "#f5f5f5" }}>
              <div style={{ fontWeight: "bold", color: "#777", letterSpacing: 1, marginBottom: 6, fontSize: 9.5, textTransform: "uppercase" }}>Notes</div>
              {QUOTE_NOTES.map((n, i) => (
                <div key={i} style={{ marginBottom: 3, fontWeight: n.bold ? "bold" : "normal" }}>• {n.text}</div>
              ))}
            </div>

          </div>{/* end inner padding */}

          {/* FOOTER */}
          <div style={{ borderTop: "1px solid #ccc", margin: "0 28px", paddingTop: 10, paddingBottom: 16, textAlign: "center", fontSize: 10.5, color: "#555" }}>
            Alliance Test Equipment, Inc. &nbsp;·&nbsp; Webster, MA 01570 &nbsp;·&nbsp; 508-671-9970 &nbsp;·&nbsp; orders@alliancetesteq.com
          </div>

        </div>{/* end paper */}

        {/* Save button — outside paper, to the right */}
        <div style={{ paddingTop: 8, flexShrink: 0 }}>
          <button
            onClick={handleGeneratePdf}
            style={{ backgroundColor: BLUE, color: "#fff", border: "none", borderRadius: 4, padding: "7px 18px", fontSize: 13, fontWeight: "bold", cursor: "pointer", boxShadow: "0 2px 6px rgba(0,0,0,0.2)", whiteSpace: "nowrap" }}
          >
            Save to PDF
          </button>
        </div>

      </div>{/* end outer wrap */}
    </div>
  );
};

export default QuoteForm;
